import React, { useState, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Space,
  Table,
  DatePicker,
  Progress,
  Select,
  Typography,
  Statistic,
  Empty,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import {
  DollarOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  FileExcelOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts'

import { invoiceStore } from '../Invoice/invoiceStore'
import { paymentStore } from '../Payment/paymentStore'
import { payoutStore } from '../Payout/payoutStore'
import { InvoiceStatus } from '@/types'
import { useMasterData } from '@/context/MasterDataContext'

const { Title, Text } = Typography

const isDateInPeriod = (
  dateVal: any,
  period: string,
  range: [Dayjs | null, Dayjs | null] | null
): boolean => {
  if (period === 'all') return true
  if (!dateVal) return true
  const d = dayjs(dateVal)
  if (!d.isValid()) return true

  const now = dayjs()
  if (period === '30d') {
    return d.isAfter(now.subtract(30, 'day').startOf('day')) || d.isSame(now, 'month')
  }
  if (period === '90d') {
    return d.isAfter(now.subtract(90, 'day').startOf('day'))
  }
  if (period === 'fy') {
    const currentYear = now.year()
    const fyStart = now.month() >= 3 ? dayjs(`${currentYear}-04-01`) : dayjs(`${currentYear - 1}-04-01`)
    const fyEnd = fyStart.add(1, 'year').subtract(1, 'day').endOf('day')
    return (d.isAfter(fyStart.startOf('day')) || d.isSame(fyStart.startOf('day'))) && (d.isBefore(fyEnd) || d.isSame(fyEnd))
  }
  if (period === 'custom') {
    if (!range || !range[0] || !range[1]) return true
    return (
      (d.isAfter(range[0].startOf('day')) || d.isSame(range[0].startOf('day'))) &&
      (d.isBefore(range[1].endOf('day')) || d.isSame(range[1].endOf('day')))
    )
  }
  return true
}

const FinanceDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { consumers, generators } = useMasterData()
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30d')
  const [customDateRange, setCustomDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  // Live store data
  const [invoices, setInvoices] = useState(() => invoiceStore.getAll())
  const [payments, setPayments] = useState(() => paymentStore.getAll())
  const [payouts, setPayouts] = useState(() => payoutStore.getAll())

  useEffect(() => {
    const sync = () => {
      setInvoices(invoiceStore.getAll())
      setPayments(paymentStore.getAll())
      setPayouts(payoutStore.getAll())
    }
    sync()
    invoiceStore.refresh().then(sync)
    paymentStore.refresh().then(sync)
    payoutStore.refresh().then(sync)

    window.addEventListener(invoiceStore.eventName, sync)
    window.addEventListener(paymentStore.eventName, sync)
    window.addEventListener(payoutStore.eventName, sync)

    return () => {
      window.removeEventListener(invoiceStore.eventName, sync)
      window.removeEventListener(paymentStore.eventName, sync)
      window.removeEventListener(payoutStore.eventName, sync)
    }
  }, [])

  // Date Filtering
  const filteredInvoices = invoices.filter((i) =>
    isDateInPeriod((i as any).invoiceDate || i.issueDate || (i as any).createdAt, selectedPeriod, customDateRange)
  )
  const filteredPayments = payments.filter((p) =>
    isDateInPeriod((p as any).paymentDate || (p as any).date || (p as any).createdAt, selectedPeriod, customDateRange)
  )
  const filteredPayouts = payouts.filter((p) =>
    isDateInPeriod((p as any).payoutDate || (p as any).date || (p as any).createdAt, selectedPeriod, customDateRange)
  )

  // KPI Calculations (Safely parsed numbers to prevent string concatenation bugs)
  const validInvoices = filteredInvoices.filter((i) => (i.status as any) !== 'rejected' && (i.status as any) !== 'REJECTED')
  const totalBilled = validInvoices.reduce((acc, i) => acc + (Number(i.totalAmount) || 0), 0)
  const totalCollected = validInvoices.reduce((acc, i) => acc + (Number(i.paidAmount) || 0), 0)
  const totalOutstanding = validInvoices.reduce(
    (acc, i) => acc + (Number((i as any).balanceAmount ?? ((Number(i.totalAmount) || 0) - (Number(i.paidAmount) || 0))) || 0),
    0
  )

  const collectionRate = totalBilled > 0 ? Math.round((totalCollected / totalBilled) * 100) : 0

  const totalPayoutsAmount = filteredPayouts.reduce(
    (acc, p) => acc + (Number((p as any).payoutAmount ?? (p as any).netAmount ?? (p as any).amount) || 0),
    0
  )
  const totalPaidPayouts = filteredPayouts
    .filter((p: any) => String(p.status).toLowerCase() === 'paid' || String(p.status).toLowerCase() === 'completed')
    .reduce(
      (acc, p) => acc + (Number((p as any).payoutAmount ?? (p as any).netAmount ?? (p as any).amount) || 0),
      0
    )

  // GST Calculation (Safely parsed numbers)
  const totalCgst = validInvoices.reduce((acc, i) => acc + (Number(i.cgstAmount) || 0), 0)
  const totalSgst = validInvoices.reduce((acc, i) => acc + (Number(i.sgstAmount) || 0), 0)
  const totalIgst = validInvoices.reduce((acc, i) => acc + (Number(i.igstAmount) || 0), 0)
  const totalGstLiability = totalCgst + totalSgst + totalIgst

  // Receivables Aging Breakdown — real per-invoice aging from each invoice's
  // own `dueDate` vs today, not a fixed percentage split of the total.
  const today = dayjs()
  const agingBuckets = { d30: 0, d60: 0, d90: 0, d90plus: 0 }
  validInvoices.forEach((i) => {
    const balance = Number(i.totalAmount || 0) - Number(i.paidAmount || 0)
    if (balance <= 0) return
    const due = dayjs(i.dueDate)
    const daysOverdue = due.isValid() ? today.diff(due, 'day') : 0
    if (daysOverdue <= 30) agingBuckets.d30 += balance
    else if (daysOverdue <= 60) agingBuckets.d60 += balance
    else if (daysOverdue <= 90) agingBuckets.d90 += balance
    else agingBuckets.d90plus += balance
  })
  const agingData = [
    { bucket: '0 - 30 Days', amount: agingBuckets.d30, fill: '#10b981' },
    { bucket: '31 - 60 Days', amount: agingBuckets.d60, fill: '#3b82f6' },
    { bucket: '61 - 90 Days', amount: agingBuckets.d90, fill: '#f59e0b' },
    { bucket: '90+ Days', amount: agingBuckets.d90plus, fill: '#ef4444' },
  ]
  const hasAgingData = agingData.some((b) => b.amount > 0)

  // Only genuinely outstanding invoices belong on an "Outstanding & Due" table.
  const outstandingInvoices = validInvoices
    .filter((i) => (Number(i.totalAmount || 0) - Number(i.paidAmount || 0)) > 0)
    .sort((a, b) => (Number(b.totalAmount || 0) - Number(b.paidAmount || 0)) - (Number(a.totalAmount || 0) - Number(a.paidAmount || 0)))

  // Outstanding Invoices Table Columns
  const invoiceColumns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (val: string, r: any) => (
        <span
          style={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#001A4D', cursor: 'pointer' }}
          onClick={() => navigate(`/invoices/${r.id}`)}
        >
          {val || r.id}
        </span>
      ),
    },
    {
      title: 'Consumer',
      dataIndex: 'entity',
      key: 'entity',
      render: (val: string) => val || 'Consumer',
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (val: number) => <strong>₹{Number(val || 0).toLocaleString()}</strong>,
    },
    {
      title: 'Outstanding Balance',
      key: 'balanceAmount',
      render: (_: any, r: any) => (
        <span style={{ color: '#ef4444', fontWeight: 700 }}>
          ₹{(Number(r.totalAmount || 0) - Number(r.paidAmount || 0)).toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: InvoiceStatus) => (
        <Tag
          color={
            status === InvoiceStatus.PAID
              ? 'success'
              : status === InvoiceStatus.OVERDUE
              ? 'error'
              : 'warning'
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, r: any) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/invoices/${r.id}`)}
          >
            View
          </Button>
          {(r.status === InvoiceStatus.ISSUED || r.status === InvoiceStatus.PARTIALLY_PAID || (r.status as any) === 'approved' || (r.status as any) === 'APPROVED') && (Number(r.totalAmount || 0) - Number(r.paidAmount || 0)) > 0 && (
            <Button
              type="primary"
              size="small"
              icon={<CreditCardOutlined />}
              onClick={() => navigate(`/payments/new?invoiceId=${r.id}`)}
            >
              Record Payment
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px', background: 'var(--surface-0)', minHeight: '100vh' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0, color: 'var(--brand-primary)', fontWeight: 700 }}>
            Finance Dashboard
          </Title>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            Invoicing Revenue, Customer Collections, Generator Payouts & GST Tax Liability
          </Text>
        </div>

        <Space wrap style={{ alignItems: 'center' }}>
          <Select
            value={selectedPeriod}
            onChange={(val) => {
              setSelectedPeriod(val)
              if (val !== 'custom') {
                setCustomDateRange(null)
              }
            }}
            style={{ minWidth: 140 }}
            options={[
              { label: 'This Month', value: '30d' },
              { label: 'This Quarter', value: '90d' },
              { label: 'Financial Year', value: 'fy' },
              { label: 'Custom Range', value: 'custom' },
              { label: 'All Time', value: 'all' },
            ]}
          />
          {selectedPeriod === 'custom' && (
            <DatePicker.RangePicker
              value={customDateRange}
              onChange={(dates) => setCustomDateRange(dates as [Dayjs | null, Dayjs | null])}
              style={{ width: 250 }}
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
            />
          )}
          <Button type="primary" icon={<FileExcelOutlined />} onClick={() => navigate('/invoices')}>
            View Invoices
          </Button>
          <Button icon={<CreditCardOutlined />} onClick={() => navigate('/payments/new')}>
            Record Payment
          </Button>
          {/* <Button icon={<ShoppingOutlined />} onClick={() => navigate('/payouts/new')}>
            Process Payout
          </Button> */}
        </Space>
      </div>

      {/* Top 4 KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Billed Revenue</Text>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ₹{totalBilled.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'nowrap' }}>
                  Invoices Total: <strong>{validInvoices.length}</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileExcelOutlined style={{ fontSize: 22, color: '#3b82f6' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #10b981' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Collections</Text>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ₹{totalCollected.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#10b981', marginTop: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <ArrowUpOutlined /> {collectionRate}% Collection Rate
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CreditCardOutlined style={{ fontSize: 22, color: '#10b981' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #ef4444' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Outstanding Receivables</Text>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ₹{totalOutstanding.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Pending Customer Payments
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ExclamationCircleOutlined style={{ fontSize: 22, color: '#ef4444' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #8b5cf6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Generator Payouts</Text>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#8b5cf6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ₹{totalPayoutsAmount.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'nowrap' }}>
                  Settled: <strong>₹{totalPaidPayouts.toLocaleString()}</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(139,92,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShoppingOutlined style={{ fontSize: 22, color: '#8b5cf6' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={14} lg={13}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>Accounts Receivable Aging Buckets (₹)</span>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
          >
            <div style={{ height: 260 }}>
              {hasAgingData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agingData} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <RechartsTooltip formatter={(val: any) => `₹${Number(val || 0).toLocaleString()}`} />
                    <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                      {agingData.map((entry, index) => (
                        <Cell key={`cell-age-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No outstanding receivables for this period" style={{ paddingTop: 60 }} />
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={10} lg={11}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>GST Tax Liability Summary</span>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 10 }}>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <Text style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>CGST Total</Text>
                <strong style={{ fontSize: 16, color: '#001A4D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>₹{totalCgst.toLocaleString()}</strong>
              </div>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <Text style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>SGST Total</Text>
                <strong style={{ fontSize: 16, color: '#001A4D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>₹{totalSgst.toLocaleString()}</strong>
              </div>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <Text style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>IGST Total</Text>
                <strong style={{ fontSize: 16, color: '#001A4D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>₹{totalIgst.toLocaleString()}</strong>
              </div>
              <div style={{ padding: '14px 16px', background: 'rgba(0,26,77,0.06)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, border: '1px solid rgba(0,26,77,0.12)' }}>
                <strong style={{ color: 'var(--brand-primary)', fontSize: 14, whiteSpace: 'nowrap', flexShrink: 0 }}>Net GST Liability</strong>
                <strong style={{ fontSize: 18, color: 'var(--brand-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>₹{totalGstLiability.toLocaleString()}</strong>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Outstanding Invoices Table */}
      <Card
        title={<span style={{ fontWeight: 700, fontSize: 16 }}>Outstanding & Due Customer Invoices</span>}
        extra={<Button type="link" onClick={() => navigate('/invoices')}>View All Invoices ({outstandingInvoices.length})</Button>}
        style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}
      >
        <Table
          dataSource={outstandingInvoices.slice(0, 5)}
          columns={invoiceColumns}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 700 }}
        />
      </Card>
    </div>
  )
}

export default FinanceDashboard
